import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText, Line, Rect } from 'react-native-svg';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const FDCalculatorScreen = () => {
  const { colors } = useTheme();
  const [depositAmount, setDepositAmount] = useState('100000');
  const [interestRate, setInterestRate] = useState(6.5);
  const [tenure, setTenure] = useState(12);
  const [tenureType, setTenureType] = useState('months');
  const [compoundingFrequency, setCompoundingFrequency] = useState('quarterly');
  const [selectedBank, setSelectedBank] = useState('sbi');
  const [isSeniorCitizen, setIsSeniorCitizen] = useState(false);
  const [taxRate, setTaxRate] = useState(30);
  const [showComparison, setShowComparison] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [results, setResults] = useState(null);

  const banks = [
    {
      name: 'State Bank of India',
      code: 'sbi',
      rates: {
        '7-45 days': 3.0,
        '46-179 days': 4.5,
        '180-210 days': 5.25,
        '211 days-1 year': 6.0,
        '1-2 years': 6.5,
        '2-3 years': 6.75,
        '3-5 years': 6.5,
        '5-10 years': 6.25,
      },
      seniorBonus: 0.5,
    },
    {
      name: 'HDFC Bank',
      code: 'hdfc',
      rates: {
        '7-14 days': 3.0,
        '15-29 days': 3.25,
        '30-90 days': 4.5,
        '91-180 days': 5.75,
        '181 days-1 year': 6.25,
        '1-2 years': 7.0,
        '2-3 years': 7.25,
        '3-5 years': 7.0,
        '5-10 years': 6.75,
      },
      seniorBonus: 0.5,
    },
    {
      name: 'ICICI Bank',
      code: 'icici',
      rates: {
        '7-14 days': 3.0,
        '15-30 days': 3.5,
        '31-90 days': 4.75,
        '91-180 days': 5.5,
        '181 days-1 year': 6.0,
        '1-2 years': 6.75,
        '2-3 years': 7.0,
        '3-5 years': 6.75,
        '5-10 years': 6.5,
      },
      seniorBonus: 0.5,
    },
    {
      name: 'Axis Bank',
      code: 'axis',
      rates: {
        '7-14 days': 3.25,
        '15-30 days': 3.75,
        '31-90 days': 5.0,
        '91-180 days': 5.75,
        '181 days-1 year': 6.25,
        '1-2 years': 7.0,
        '2-3 years': 7.25,
        '3-5 years': 7.0,
        '5-10 years': 6.75,
      },
      seniorBonus: 0.5,
    },
    {
      name: 'Punjab National Bank',
      code: 'pnb',
      rates: {
        '7-14 days': 3.0,
        '15-45 days': 4.0,
        '46-90 days': 4.5,
        '91-180 days': 5.25,
        '181 days-1 year': 5.75,
        '1-2 years': 6.25,
        '2-3 years': 6.5,
        '3-5 years': 6.25,
        '5-10 years': 6.0,
      },
      seniorBonus: 0.5,
    },
  ];

  const compoundingOptions = [
    { label: 'Monthly', value: 'monthly', frequency: 12 },
    { label: 'Quarterly', value: 'quarterly', frequency: 4 },
    { label: 'Half-Yearly', value: 'half-yearly', frequency: 2 },
    { label: 'Yearly', value: 'yearly', frequency: 1 },
  ];

  const tenureOptions = [
    { label: 'Days', value: 'days' },
    { label: 'Months', value: 'months' },
    { label: 'Years', value: 'years' },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const getBankRate = (bankCode, tenureInDays) => {
    const bank = banks.find(b => b.code === bankCode);
    if (!bank) return 6.0;

    let rate = 6.0;
    
    if (tenureInDays <= 14) rate = bank.rates['7-14 days'] || 3.0;
    else if (tenureInDays <= 30) rate = bank.rates['15-30 days'] || bank.rates['15-29 days'] || 3.5;
    else if (tenureInDays <= 45) rate = bank.rates['31-45 days'] || bank.rates['30-90 days'] || 4.0;
    else if (tenureInDays <= 90) rate = bank.rates['46-90 days'] || bank.rates['31-90 days'] || 4.5;
    else if (tenureInDays <= 180) rate = bank.rates['91-180 days'] || 5.5;
    else if (tenureInDays <= 365) rate = bank.rates['181 days-1 year'] || 6.0;
    else if (tenureInDays <= 730) rate = bank.rates['1-2 years'] || 6.5;
    else if (tenureInDays <= 1095) rate = bank.rates['2-3 years'] || 6.75;
    else if (tenureInDays <= 1825) rate = bank.rates['3-5 years'] || 6.5;
    else rate = bank.rates['5-10 years'] || 6.25;

    // Add senior citizen bonus
    if (isSeniorCitizen) {
      rate += bank.seniorBonus;
    }

    return rate;
  };

  const calculateFD = () => {
    const principal = parseFloat(depositAmount) || 0;
    let tenureInDays = parseFloat(tenure) || 0;
    
    // Convert tenure to days
    if (tenureType === 'months') {
      tenureInDays = tenureInDays * 30.44; // Average days per month
    } else if (tenureType === 'years') {
      tenureInDays = tenureInDays * 365.25; // Including leap years
    }

    // Get bank-specific rate or use manual rate
    const rate = selectedBank ? getBankRate(selectedBank, tenureInDays) : interestRate;
    
    const compounding = compoundingOptions.find(c => c.value === compoundingFrequency);
    const n = compounding.frequency;
    const t = tenureInDays / 365.25; // Convert to years

    // Calculate compound interest
    const maturityAmount = principal * Math.pow(1 + (rate / 100) / n, n * t);
    const interestEarned = maturityAmount - principal;

    // Calculate TDS (Tax Deducted at Source)
    const tdsThreshold = 40000; // TDS applicable if interest > 40,000
    const tdsRate = 0.1; // 10% TDS
    let tdsAmount = 0;
    
    if (interestEarned > tdsThreshold) {
      tdsAmount = interestEarned * tdsRate;
    }

    // Calculate tax on interest (as per income tax slab)
    const taxOnInterest = interestEarned * (taxRate / 100);
    const postTaxInterest = interestEarned - taxOnInterest;
    const postTaxMaturity = principal + postTaxInterest;

    // Calculate effective rate
    const effectiveRate = ((maturityAmount / principal) ** (1 / t) - 1) * 100;

    // Calculate monthly breakdown for visualization
    const monthlyBreakdown = [];
    const monthsTotal = Math.ceil(tenureInDays / 30.44);
    
    for (let month = 1; month <= Math.min(monthsTotal, 60); month++) {
      const monthlyTime = (month * 30.44) / 365.25;
      const monthlyAmount = principal * Math.pow(1 + (rate / 100) / n, n * monthlyTime);
      monthlyBreakdown.push({
        month,
        amount: monthlyAmount,
        interest: monthlyAmount - principal,
      });
    }

    return {
      principal,
      maturityAmount,
      interestEarned,
      rate,
      effectiveRate,
      tdsAmount,
      taxOnInterest,
      postTaxInterest,
      postTaxMaturity,
      tenureInDays,
      monthlyBreakdown,
    };
  };

  useEffect(() => {
    const calculatedResults = calculateFD();
    setResults(calculatedResults);
  }, [
    depositAmount, interestRate, tenure, tenureType, compoundingFrequency, 
    selectedBank, isSeniorCitizen, taxRate
  ]);

  const renderHeader = () => (
    <View style={styles.header}>
      <Text style={styles.headerTitle}>Fixed Deposit Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate FD returns with bank-specific rates, tax implications, and senior citizen benefits
      </Text>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorCard}>
      <Text style={styles.cardTitle}>FD Details</Text>
      
      {/* Deposit Amount */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Deposit Amount</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={depositAmount}
            onChangeText={setDepositAmount}
            placeholder="100000"
            keyboardType="numeric"
          />
        </View>
      </View>

      {/* Bank Selection */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Select Bank</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.bankScroll}>
          <View style={styles.bankContainer}>
            <TouchableOpacity
              style={[
                styles.bankCard,
                !selectedBank && styles.bankCardActive,
              ]}
              onPress={() => setSelectedBank(null)}
            >
              <Text style={[
                styles.bankName,
                !selectedBank && styles.bankNameActive,
              ]}>
                Custom Rate
              </Text>
              <Text style={[
                styles.bankRate,
                !selectedBank && styles.bankRateActive,
              ]}>
                Manual Entry
              </Text>
            </TouchableOpacity>
            {banks.map((bank) => (
              <TouchableOpacity
                key={bank.code}
                style={[
                  styles.bankCard,
                  selectedBank === bank.code && styles.bankCardActive,
                ]}
                onPress={() => setSelectedBank(bank.code)}
              >
                <Text style={[
                  styles.bankName,
                  selectedBank === bank.code && styles.bankNameActive,
                ]}>
                  {bank.name}
                </Text>
                <Text style={[
                  styles.bankRate,
                  selectedBank === bank.code && styles.bankRateActive,
                ]}>
                  Up to {Math.max(...Object.values(bank.rates))}%
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>

      {/* Interest Rate (if custom) */}
      {!selectedBank && (
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Interest Rate: {interestRate}%</Text>
          <Slider
            style={styles.slider}
            minimumValue={3}
            maximumValue={12}
            value={interestRate}
            onValueChange={setInterestRate}
            step={0.25}
            minimumTrackTintColor={colors.primary}
            maximumTrackTintColor={colors.border}
            thumbStyle={styles.sliderThumb}
          />
          <View style={styles.sliderLabels}>
            <Text style={styles.sliderLabel}>3%</Text>
            <Text style={styles.sliderLabel}>12%</Text>
          </View>
        </View>
      )}

      {/* Tenure */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Tenure</Text>
        <View style={styles.tenureContainer}>
          <View style={styles.tenureInputContainer}>
            <TextInput
              style={styles.tenureInput}
              value={tenure.toString()}
              onChangeText={(value) => setTenure(parseFloat(value) || 0)}
              placeholder="12"
              keyboardType="numeric"
            />
          </View>
          <View style={styles.tenureTypeContainer}>
            {tenureOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.tenureTypeButton,
                  tenureType === option.value && styles.tenureTypeButtonActive,
                ]}
                onPress={() => setTenureType(option.value)}
              >
                <Text style={[
                  styles.tenureTypeText,
                  tenureType === option.value && styles.tenureTypeTextActive,
                ]}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      {/* Compounding Frequency */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Compounding Frequency</Text>
        <View style={styles.segmentedControl}>
          {compoundingOptions.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.segmentButton,
                compoundingFrequency === option.value && styles.segmentButtonActive,
              ]}
              onPress={() => setCompoundingFrequency(option.value)}
            >
              <Text style={[
                styles.segmentButtonText,
                compoundingFrequency === option.value && styles.segmentButtonTextActive,
              ]}>
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Senior Citizen */}
      <View style={styles.inputGroup}>
        <TouchableOpacity
          style={styles.checkboxContainer}
          onPress={() => setIsSeniorCitizen(!isSeniorCitizen)}
        >
          <View style={[styles.checkbox, isSeniorCitizen && styles.checkboxActive]}>
            {isSeniorCitizen && (
              <Ionicons name="checkmark" size={16} color={colors.background} />
            )}
          </View>
          <Text style={styles.checkboxLabel}>Senior Citizen (Additional 0.5% interest)</Text>
        </TouchableOpacity>
      </View>

      {/* Tax Rate */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Tax Rate: {taxRate}%</Text>
        <Slider
          style={styles.slider}
          minimumValue={0}
          maximumValue={30}
          value={taxRate}
          onValueChange={setTaxRate}
          step={5}
          minimumTrackTintColor={colors.primary}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
        <View style={styles.sliderLabels}>
          <Text style={styles.sliderLabel}>0%</Text>
          <Text style={styles.sliderLabel}>30%</Text>
        </View>
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const { principal, interestEarned, maturityAmount } = results;
    const principalPercentage = (principal / maturityAmount) * 100;
    const interestPercentage = (interestEarned / maturityAmount) * 100;

    const size = 200;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;

    const principalStrokeDasharray = `${(principalPercentage / 100) * circumference} ${circumference}`;
    const interestStrokeDasharray = `${(interestPercentage / 100) * circumference} ${circumference}`;
    const interestStrokeDashoffset = -((principalPercentage / 100) * circumference);

    return (
      <View style={styles.chartCard}>
        <Text style={styles.cardTitle}>FD Breakdown</Text>
        <View style={styles.chartContainer}>
          <Svg width={size} height={size}>
            {/* Background circle */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.border}
              strokeWidth={strokeWidth}
              fill="transparent"
            />
            
            {/* Principal amount */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.primary}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={principalStrokeDasharray}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Interest earned */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.success}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={interestStrokeDasharray}
              strokeDashoffset={interestStrokeDashoffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Center text */}
            <SvgText
              x={size / 2}
              y={size / 2 - 10}
              textAnchor="middle"
              fontSize="14"
              fontWeight="600"
              fill={colors.text}
            >
              Maturity Amount
            </SvgText>
            <SvgText
              x={size / 2}
              y={size / 2 + 10}
              textAnchor="middle"
              fontSize="16"
              fontWeight="bold"
              fill={colors.text}
            >
              {formatCurrency(maturityAmount)}
            </SvgText>
          </Svg>
        </View>
        
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.primary }]} />
            <Text style={styles.legendText}>Principal ({principalPercentage.toFixed(1)}%)</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.success }]} />
            <Text style={styles.legendText}>Interest ({interestPercentage.toFixed(1)}%)</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    const { 
      principal, 
      maturityAmount, 
      interestEarned, 
      rate,
      effectiveRate,
      tdsAmount,
      taxOnInterest,
      postTaxInterest,
      postTaxMaturity,
      tenureInDays
    } = results;

    return (
      <View style={styles.resultsCard}>
        <Text style={styles.cardTitle}>FD Results</Text>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Principal Amount</Text>
          <Text style={styles.resultValue}>{formatCurrency(principal)}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Interest Rate</Text>
          <Text style={styles.resultValue}>{rate.toFixed(2)}%</Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Tenure</Text>
          <Text style={styles.resultValue}>{Math.round(tenureInDays)} days</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Maturity Amount</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(maturityAmount)}
          </Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Interest Earned</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(interestEarned)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Effective Rate</Text>
          <Text style={styles.resultValue}>{effectiveRate.toFixed(2)}%</Text>
        </View>

        {tdsAmount > 0 && (
          <View style={styles.resultItem}>
            <Text style={styles.resultLabel}>TDS (10%)</Text>
            <Text style={[styles.resultValue, { color: colors.error }]}>
              {formatCurrency(tdsAmount)}
            </Text>
          </View>
        )}

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Tax on Interest</Text>
          <Text style={[styles.resultValue, { color: colors.error }]}>
            {formatCurrency(taxOnInterest)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Post-Tax Maturity</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(postTaxMaturity)}
          </Text>
        </View>
      </View>
    );
  };

  const renderGrowthChart = () => {
    if (!results || !results.monthlyBreakdown) return null;

    const { monthlyBreakdown, principal } = results;
    const maxAmount = Math.max(...monthlyBreakdown.map(item => item.amount));
    const chartHeight = 200;
    const chartWidth = 300;
    const padding = 20;

    return (
      <View style={styles.growthCard}>
        <Text style={styles.cardTitle}>Growth Visualization</Text>
        <View style={styles.growthContainer}>
          <Svg width={chartWidth} height={chartHeight + padding * 2}>
            {/* Draw bars */}
            {monthlyBreakdown.slice(0, 12).map((item, index) => {
              const barHeight = ((item.amount - principal) / (maxAmount - principal)) * chartHeight;
              const barWidth = (chartWidth - padding * 2) / 12;
              const x = padding + index * barWidth;
              const y = chartHeight + padding - barHeight;

              return (
                <Rect
                  key={index}
                  x={x}
                  y={y}
                  width={barWidth - 2}
                  height={barHeight}
                  fill={colors.primary}
                  opacity={0.8}
                />
              );
            })}
            
            {/* X-axis */}
            <Line
              x1={padding}
              y1={chartHeight + padding}
              x2={chartWidth - padding}
              y2={chartHeight + padding}
              stroke={colors.border}
              strokeWidth={1}
            />
            
            {/* Y-axis */}
            <Line
              x1={padding}
              y1={padding}
              x2={padding}
              y2={chartHeight + padding}
              stroke={colors.border}
              strokeWidth={1}
            />
          </Svg>
        </View>
        <Text style={styles.chartCaption}>Monthly Interest Growth (First 12 months)</Text>
      </View>
    );
  };

  const renderComparison = () => {
    if (!results) return null;

    const { effectiveRate, postTaxMaturity, principal } = results;
    const tenureYears = results.tenureInDays / 365.25;
    
    // Compare with other investment options
    const savingsAccount = principal * Math.pow(1.035, tenureYears); // 3.5% savings
    const ppf = principal * Math.pow(1.075, tenureYears); // 7.5% PPF
    const mutualFund = principal * Math.pow(1.12, tenureYears); // 12% mutual fund

    const comparisons = [
      { name: 'Fixed Deposit', value: postTaxMaturity, returns: effectiveRate, color: colors.primary },
      { name: 'Savings Account', value: savingsAccount, returns: 3.5, color: colors.warning },
      { name: 'PPF', value: ppf, returns: 7.5, color: colors.success },
      { name: 'Mutual Fund (Equity)', value: mutualFund, returns: 12, color: '#8B5CF6' },
    ];

    return (
      <View style={styles.comparisonCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowComparison(!showComparison)}
        >
          <Text style={styles.cardTitle}>Investment Comparison</Text>
          <Ionicons
            name={showComparison ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showComparison && (
          <View style={styles.cardContent}>
            {comparisons.map((item, index) => (
              <View key={index} style={styles.comparisonItem}>
                <View style={styles.comparisonHeader}>
                  <View style={[styles.comparisonIndicator, { backgroundColor: item.color }]} />
                  <Text style={styles.comparisonName}>{item.name}</Text>
                  <Text style={styles.comparisonReturns}>{item.returns.toFixed(1)}%</Text>
                </View>
                <Text style={styles.comparisonValue}>{formatCurrency(item.value)}</Text>
                <View style={styles.comparisonBar}>
                  <View 
                    style={[
                      styles.comparisonBarFill, 
                      { 
                        width: `${(item.value / Math.max(...comparisons.map(c => c.value))) * 100}%`,
                        backgroundColor: item.color 
                      }
                    ]} 
                  />
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>Fixed Deposit Calculator - Plan Your Safe Investments</Text>
      <Text style={styles.infoSubtitle}>Calculate FD Returns, Compare Banks & Optimize Your Fixed Deposit Strategy</Text>
      
      <Text style={styles.infoText}>
        A Fixed Deposit (FD) Calculator is an essential financial tool that helps you calculate the maturity amount 
        and interest earnings on your fixed deposit investments. Fixed deposits are one of India's most popular 
        investment options, offering guaranteed returns with capital protection. Our comprehensive FD calculator 
        considers bank-specific interest rates, compounding frequency, senior citizen benefits, and tax implications 
        to provide accurate maturity calculations.
      </Text>

      <Text style={styles.infoSubheading}>What is a Fixed Deposit?</Text>
      <Text style={styles.infoText}>
        A Fixed Deposit is a financial instrument where you deposit a lump sum amount with a bank or financial 
        institution for a predetermined period at a fixed interest rate. Unlike savings accounts, FDs offer 
        higher interest rates and guaranteed returns, making them ideal for risk-averse investors seeking 
        stable income and capital preservation.
      </Text>

      <Text style={styles.infoSubheading}>Key Benefits of Fixed Deposits</Text>
      <Text style={styles.infoText}>
        • <Text style={{ fontWeight: 'bold' }}>Guaranteed Returns:</Text> Fixed interest rates ensure predictable earnings{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Capital Protection:</Text> Principal amount is completely safe{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Flexible Tenure:</Text> Choose from 7 days to 10 years{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Deposit Insurance:</Text> Protected up to ₹5 lakhs per bank{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Senior Citizen Benefits:</Text> Additional 0.25% to 0.5% interest{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Loan Against FD:</Text> Get loans up to 90% of FD value{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Tax Benefits:</Text> Tax-saving FDs under Section 80C
      </Text>

      <Text style={styles.infoSubheading}>How FD Interest is Calculated</Text>
      <Text style={styles.infoText}>
        FD interest is calculated using the compound interest formula:{'\n'}
        <Text style={{ fontWeight: 'bold' }}>A = P × (1 + r/n)^(n×t)</Text>{'\n\n'}
        Where:{'\n'}
        • A = Maturity Amount{'\n'}
        • P = Principal (Initial Deposit){'\n'}
        • r = Annual Interest Rate{'\n'}
        • n = Compounding Frequency (Quarterly/Half-yearly/Yearly){'\n'}
        • t = Time Period in Years
      </Text>

      <Text style={styles.infoSubheading}>How to Use Our FD Calculator</Text>
      <Text style={styles.infoText}>
        1. <Text style={{ fontWeight: 'bold' }}>Enter Deposit Amount:</Text> Input your investment amount{'\n'}
        2. <Text style={{ fontWeight: 'bold' }}>Select Bank:</Text> Choose from major banks with current rates{'\n'}
        3. <Text style={{ fontWeight: 'bold' }}>Set Tenure:</Text> Select investment period in days, months, or years{'\n'}
        4. <Text style={{ fontWeight: 'bold' }}>Choose Compounding:</Text> Select quarterly, half-yearly, or yearly{'\n'}
        5. <Text style={{ fontWeight: 'bold' }}>Senior Citizen:</Text> Enable if you're 60+ years old{'\n'}
        6. <Text style={{ fontWeight: 'bold' }}>Set Tax Rate:</Text> Enter your income tax slab rate{'\n'}
        7. <Text style={{ fontWeight: 'bold' }}>View Results:</Text> Get detailed maturity and tax calculations
      </Text>

      <Text style={styles.infoSubheading}>Types of Fixed Deposits</Text>
      <Text style={styles.infoText}>
        • <Text style={{ fontWeight: 'bold' }}>Regular FD:</Text> Standard fixed deposits with flexible tenure{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Tax-Saving FD:</Text> 5-year lock-in with Section 80C benefits{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Senior Citizen FD:</Text> Higher rates for 60+ age group{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Cumulative FD:</Text> Interest compounded and paid at maturity{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Non-Cumulative FD:</Text> Interest paid periodically{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Flexi FD:</Text> Automatic sweep-in/sweep-out facility
      </Text>

      <Text style={styles.infoSubheading}>FD Investment Best Practices</Text>
      <Text style={styles.infoText}>
        • <Text style={{ fontWeight: 'bold' }}>Laddering Strategy:</Text> Spread investments across different tenures{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Rate Comparison:</Text> Compare rates across multiple banks{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Tenure Selection:</Text> Align with your financial goals{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Tax Planning:</Text> Consider tax implications on interest{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Diversification:</Text> Don't put all money in single bank{'\n'}
        • <Text style={{ fontWeight: 'bold' }}>Auto-Renewal:</Text> Set up automatic renewal for convenience
      </Text>
      
      <View style={styles.tipBox}>
        <Ionicons name="shield-checkmark" size={16} color={colors.success} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Deposit Insurance:</Text> All FDs are covered under DICGC 
          (Deposit Insurance and Credit Guarantee Corporation) up to ₹5 lakhs per depositor per bank, 
          making them one of the safest investment options in India.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="trending-up" size={16} color={colors.primary} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Senior Citizen Benefits:</Text> If you're 60 years or older, 
          most banks offer additional interest of 0.25% to 0.5% on regular FD rates. Some banks extend 
          this benefit to 55+ years for women depositors.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="calculator" size={16} color={colors.warning} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Tax Implications:</Text> FD interest is taxable as per your 
          income tax slab. Banks deduct TDS at 10% if annual interest exceeds ₹40,000. Submit Form 15G/15H 
          if your total income is below taxable limit to avoid TDS.
        </Text>
      </View>

      <View style={styles.tipBox}>
        <Ionicons name="bulb" size={16} color={colors.info} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Pro Tip:</Text> Consider cumulative FDs for better returns 
          due to compounding effect. For regular income needs, choose non-cumulative FDs with monthly 
          or quarterly interest payouts.
        </Text>
      </View>
    </View>
  );

  const renderFAQ = () => {
    const faqData = [
      {
        question: "What is a Fixed Deposit (FD) and how does it work?",
        answer: "A Fixed Deposit is a financial instrument where you deposit money with a bank for a fixed period at a predetermined interest rate. The bank pays you interest on your deposit, and at maturity, you receive both principal and accumulated interest. FDs offer guaranteed returns and are considered one of the safest investment options."
      },
      {
        question: "What is the minimum amount required to open an FD?",
        answer: "Most banks require a minimum deposit of ₹1,000 to ₹10,000 to open a Fixed Deposit account. However, this varies by bank and FD type. Some banks offer FDs with as low as ₹100 minimum deposit, while others may require higher amounts for premium FD products."
      },
      {
        question: "How is FD interest calculated and what is compounding?",
        answer: "FD interest is calculated using compound interest formula: A = P(1 + r/n)^(nt). Compounding means earning interest on both principal and previously earned interest. Most banks offer quarterly compounding, which gives better returns than simple interest. The frequency of compounding (quarterly, half-yearly, yearly) affects your final maturity amount."
      },
      {
        question: "Can I withdraw my FD before maturity?",
        answer: "Yes, premature withdrawal is allowed but banks typically charge a penalty of 0.5% to 1% on the applicable interest rate. Some banks may not pay any interest for very short-term withdrawals (less than 7 days). The penalty varies by bank and tenure completed, so check terms before investing."
      },
      {
        question: "What is the difference between cumulative and non-cumulative FDs?",
        answer: "Cumulative FDs reinvest the interest, leading to compounding and higher returns at maturity. Non-cumulative FDs pay interest periodically (monthly, quarterly, half-yearly, or yearly) without compounding, resulting in lower overall returns but providing regular income."
      },
      {
        question: "Are FDs safe and what is deposit insurance?",
        answer: "FDs are extremely safe as they're covered under DICGC (Deposit Insurance and Credit Guarantee Corporation) up to ₹5 lakhs per depositor per bank. This means even if a bank fails, your deposit up to ₹5 lakhs is guaranteed by the government. For amounts above ₹5 lakhs, consider spreading across multiple banks."
      },
      {
        question: "What are the tax implications of FD investments?",
        answer: "FD interest is taxable as per your income tax slab rate. Banks deduct TDS (Tax Deducted at Source) at 10% if annual interest exceeds ₹40,000. If your total income is below taxable limit, submit Form 15G (for individuals below 60) or Form 15H (for senior citizens) to avoid TDS deduction."
      },
      {
        question: "Do senior citizens get higher FD interest rates?",
        answer: "Yes, most banks offer additional interest of 0.25% to 0.5% for senior citizens (60+ years). Some banks extend this benefit to women depositors from 55 years. This additional rate applies to regular FDs and can significantly boost returns over time."
      },
      {
        question: "What is the ideal tenure for FD investments?",
        answer: "FD tenure should align with your financial goals and liquidity needs. Short-term FDs (3-12 months) offer liquidity but lower rates. Medium-term FDs (1-3 years) balance returns and liquidity. Long-term FDs (3-10 years) offer higher rates but lock your money. Consider laddering strategy for optimal returns."
      },
      {
        question: "Can I get a loan against my FD?",
        answer: "Yes, most banks offer loans against FDs up to 90-95% of the FD value. The interest rate on such loans is typically 1-2% higher than your FD rate. This allows you to access funds without breaking your FD, and you continue earning interest on the full FD amount."
      },
      {
        question: "What is FD laddering and how does it help?",
        answer: "FD laddering involves dividing your investment across multiple FDs with different maturity dates. For example, instead of investing ₹5 lakhs in one 5-year FD, invest ₹1 lakh each in 1, 2, 3, 4, and 5-year FDs. This provides regular liquidity and helps you reinvest at potentially higher rates."
      },
      {
        question: "How do FD rates compare across different banks?",
        answer: "FD rates vary significantly across banks. Public sector banks typically offer 5.5-7% for regular FDs, while private banks may offer 6-8%. Small finance banks and NBFCs often provide higher rates (7-9%) but carry slightly higher risk. Always compare rates and check bank credibility before investing."
      },
      {
        question: "What is a tax-saving FD and how is it different?",
        answer: "Tax-saving FDs have a mandatory 5-year lock-in period and qualify for deduction up to ₹1.5 lakhs under Section 80C of Income Tax Act. However, the interest earned is still taxable. These FDs typically offer slightly lower rates than regular FDs due to the tax benefit."
      },
      {
        question: "Should I choose auto-renewal for my FD?",
        answer: "Auto-renewal ensures your FD continues earning interest without gaps, but you might miss better rates available in the market. It's convenient for passive investors but active investors should review rates at maturity and consider switching banks for better returns. You can usually modify auto-renewal instructions anytime."
      },
      {
        question: "How does inflation affect FD returns?",
        answer: "Inflation erodes the purchasing power of your FD returns. If inflation is 6% and your FD gives 7%, your real return is only 1%. During high inflation periods, FD returns may not beat inflation, making them less attractive. Consider this while planning long-term investments and diversify accordingly."
      }
    ];

    return (
      <View style={styles.faqContainer}>
        <TouchableOpacity
          style={styles.faqHeader}
          onPress={() => setShowFAQ(!showFAQ)}
        >
          <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
          <Ionicons
            name={showFAQ ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showFAQ && (
          <View style={styles.faqContent}>
            {faqData.map((faq, index) => (
              <View key={index} style={styles.faqItem}>
                <Text style={styles.faqQuestion}>{faq.question}</Text>
                <Text style={styles.faqAnswer}>{faq.answer}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.surface,
      paddingVertical: 40,
      paddingHorizontal: 20,
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: {
      fontSize: 32,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    headerSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    content: {
      flexDirection: isMobile ? 'column' : 'row',
      padding: 20,
      gap: 20,
      width: '100%',
    },
    leftPanel: {
      flex: isMobile ? 1 : 1.2,
    },
    rightPanel: {
      flex: 1,
      gap: 20,
    },
    calculatorCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      paddingLeft: 12,
    },
    textInput: {
      flex: 1,
      height: 48,
      paddingHorizontal: 12,
      fontSize: 16,
      color: colors.text,
    },
    bankScroll: {
      marginTop: 8,
    },
    bankContainer: {
      flexDirection: 'row',
      gap: 12,
      paddingRight: 20,
    },
    bankCard: {
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 12,
      minWidth: 140,
      borderWidth: 1,
      borderColor: colors.border,
    },
    bankCardActive: {
      backgroundColor: colors.primary + '20',
      borderColor: colors.primary,
    },
    bankName: {
      fontSize: 12,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
    },
    bankNameActive: {
      color: colors.primary,
    },
    bankRate: {
      fontSize: 10,
      color: colors.textSecondary,
    },
    bankRateActive: {
      color: colors.primary,
    },
    tenureContainer: {
      flexDirection: 'row',
      gap: 12,
      alignItems: 'center',
    },
    tenureInputContainer: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    tenureInput: {
      height: 48,
      paddingHorizontal: 12,
      fontSize: 16,
      color: colors.text,
    },
    tenureTypeContainer: {
      flexDirection: 'row',
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 2,
    },
    tenureTypeButton: {
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: 6,
    },
    tenureTypeButtonActive: {
      backgroundColor: colors.primary,
    },
    tenureTypeText: {
      fontSize: 12,
      color: colors.textSecondary,
      fontWeight: '500',
    },
    tenureTypeTextActive: {
      color: colors.background,
    },
    segmentedControl: {
      flexDirection: 'row',
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 4,
    },
    segmentButton: {
      flex: 1,
      paddingVertical: 12,
      paddingHorizontal: 8,
      borderRadius: 6,
      alignItems: 'center',
    },
    segmentButtonActive: {
      backgroundColor: colors.primary,
    },
    segmentButtonText: {
      fontSize: 12,
      color: colors.textSecondary,
      fontWeight: '500',
    },
    segmentButtonTextActive: {
      color: colors.background,
    },
    checkboxContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderWidth: 2,
      borderColor: colors.border,
      borderRadius: 4,
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkboxActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    checkboxLabel: {
      fontSize: 14,
      color: colors.text,
    },
    slider: {
      height: 40,
      marginVertical: 10,
    },
    sliderThumb: {
      backgroundColor: colors.primary,
      width: 20,
      height: 20,
    },
    sliderLabels: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    sliderLabel: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    chartCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      alignItems: 'center',
    },
    chartContainer: {
      alignItems: 'center',
      marginVertical: 20,
    },
    chartLegend: {
      marginTop: 20,
      gap: 8,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    legendText: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultsCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    resultItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    resultLabel: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    growthCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      alignItems: 'center',
    },
    growthContainer: {
      alignItems: 'center',
      marginVertical: 20,
    },
    chartCaption: {
      fontSize: 12,
      color: colors.textSecondary,
      marginTop: 10,
      textAlign: 'center',
    },
    comparisonCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    cardContent: {
      marginTop: 20,
    },
    comparisonItem: {
      marginBottom: 16,
      padding: 12,
      backgroundColor: colors.background,
      borderRadius: 8,
    },
    comparisonHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
    },
    comparisonIndicator: {
      width: 12,
      height: 12,
      borderRadius: 6,
      marginRight: 8,
    },
    comparisonName: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      flex: 1,
    },
    comparisonReturns: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    comparisonValue: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    comparisonBar: {
      height: 4,
      backgroundColor: colors.border,
      borderRadius: 2,
      overflow: 'hidden',
    },
    comparisonBarFill: {
      height: '100%',
      borderRadius: 2,
    },
    infoContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 24,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    infoTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    infoSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      marginBottom: 16,
    },
    infoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
      marginBottom: 16,
    },
    tipBox: {
      flexDirection: 'row',
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 12,
      marginTop: 8,
    },
    tipText: {
      fontSize: 13,
      color: colors.textSecondary,
      marginLeft: 8,
      flex: 1,
      lineHeight: 18,
    },
    faqContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      marginTop: 20,
    },
    faqItem: {
      marginBottom: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    fullWidthSection: {
      paddingHorizontal: 20,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
          {renderGrowthChart()}
        </View>
      </View>
      
      {renderComparison()}
      {renderInformation()}
      
      <View style={styles.fullWidthSection}>
        {renderFAQ()}
      </View>
      
      <Footer />
    </ScrollView>
  );
};

export default FDCalculatorScreen;